/**
 * Custom hook for recipient selection logic
 * Consolidates common patterns across all sharing components
 */

import { useCallback, useMemo } from 'react';
import { Recipient, RecipientSelection, RecipientUtils } from './types';
import { useMyGroups, useAllGroups } from '../../../hooks/api/useGroups';
import { useUsersForSharing } from '../../../hooks/api/useUsersForSharing';
import { useRecentGroups, useRecentUsers } from '../../../stores/sharingStore';
import { usePermissions } from '../../../hooks/permissions/usePermissions';
import { log } from '../../../utils/errors/logger';

interface UseRecipientSelectionOptions {
  /** Current selection */
  value: RecipientSelection;
  /** Callback when selection changes */
  onChange: (selection: RecipientSelection) => void;
  /** Whether to show users */
  showUsers?: boolean;
  /** Whether to show groups */
  showGroups?: boolean;
  /** Whether user can see all groups (moderator/admin) */
  canSeeAllGroups?: boolean;
}

export const useRecipientSelection = ({
  value,
  onChange,
  showUsers = true,
  showGroups = true,
  canSeeAllGroups: explicitCanSeeAllGroups
}: UseRecipientSelectionOptions) => {
  // Determine user permissions
  const { can } = usePermissions();
  const hasModeratorPermissions = can.manageUsers;
  const canSeeAllGroups = explicitCanSeeAllGroups ?? hasModeratorPermissions;

  // Load data based on permissions and configuration
  const { data: myGroupsResponse, isLoading: myGroupsLoading } = useMyGroups(undefined, { enabled: showGroups });
  const { data: allGroupsResponse, isLoading: allGroupsLoading } = useAllGroups(Boolean(canSeeAllGroups && showGroups));
  const { data: usersData, isLoading: usersLoading } = useUsersForSharing({ enabled: showUsers });

  // Use appropriate groups data
  const groupsData = useMemo(() => {
    if (!showGroups) {return [];}
    const allGroups = allGroupsResponse?.groups || [];
    const myGroups = myGroupsResponse?.groups || [];
    return canSeeAllGroups ? allGroups : myGroups;
  }, [showGroups, canSeeAllGroups, allGroupsResponse, myGroupsResponse]);

  const usersDataList = useMemo(() => {
    return showUsers ? (usersData || []) : [];
  }, [showUsers, usersData]);

  // Loading states
  const isLoadingGroups = showGroups && (canSeeAllGroups ? allGroupsLoading : myGroupsLoading);
  const isLoadingUsers = showUsers && usersLoading;
  const isLoading = isLoadingGroups || isLoadingUsers;

  // Recently used items
  const { recentlyUsedGroups, addRecentGroup } = useRecentGroups();
  const { recentlyUsedUsers, addRecentUser } = useRecentUsers();

  // Convert groups and users to Recipient format
  const availableGroups: Recipient[] = useMemo(() => {
    return groupsData.map(group => ({
      id: group.id,
      type: 'group' as const,
      name: group.displayName,
      subtitle: group.description ?? undefined,
      description: group.description ?? undefined,
      icon: '👥',
      source: group.source
    }));
  }, [groupsData]);

  const availableUsers: Recipient[] = useMemo(() => {
    const users = usersDataList.map(user => ({
      id: user.id,
      type: 'user' as const,
      name: user.display_name || user.username || user.email || '',
      subtitle: user.email,
      description: user.role,
      icon: '👤'
    }));

    // Debug logging
    if (users.length > 0) {
      log.user.action('users_mapped', 'RecipientPicker', {
        count: users.length,
        sampleId: users[0].id,
        sampleName: users[0].name
      });
    }

    return users;
  }, [usersDataList]);

  // Get recently used groups
  const recentGroups = useMemo(() => {
    return availableGroups.filter(g => recentlyUsedGroups.includes(g.id));
  }, [availableGroups, recentlyUsedGroups]);

  // Get recently used users
  const recentUsers = useMemo(() => {
    return availableUsers.filter(u => recentlyUsedUsers.includes(u.id));
  }, [availableUsers, recentlyUsedUsers]);

  // Get available (not selected) recipients
  const availableGroupsFiltered = useMemo(() => {
    return availableGroups.filter(g => !value.groups.includes(g.id));
  }, [availableGroups, value.groups]);

  const availableUsersFiltered = useMemo(() => {
    return availableUsers.filter(u => !value.users.includes(u.id));
  }, [availableUsers, value.users]);

  // Get selected recipients with full data
  const selectedRecipients: Recipient[] = useMemo(() => {
    const selected: Recipient[] = [];

    // Add selected users
    value.users.forEach(userId => {
      const user = availableUsers.find(u => u.id === userId);
      if (user) {
        selected.push(user);
      } else {
        // Create stub for missing user
        selected.push({
          id: userId,
          type: 'user',
          name: `User ${userId.substring(0, 8)}...`,
          subtitle: 'Loading...'
        });
      }
    });

    // Add selected groups
    value.groups.forEach(groupId => {
      const group = availableGroups.find(g => g.id === groupId);
      if (group) {
        selected.push(group);
      } else {
        // Create stub for missing group
        selected.push({
          id: groupId,
          type: 'group',
          name: `Group ${groupId.substring(0, 8)}...`,
          subtitle: 'Loading...'
        });
      }
    });

    return selected;
  }, [value, availableUsers, availableGroups]);

  // Add recipient handler
  const addRecipient = useCallback((recipient: Recipient) => {
    log.user.action('addRecipient_called', 'RecipientPicker', {
      recipientId: recipient.id,
      recipientType: recipient.type,
      recipientName: recipient.name
    });

    const newSelection = { ...value };

    if (recipient.type === 'user') {
      if (!newSelection.users.includes(recipient.id)) {
        newSelection.users = [...newSelection.users, recipient.id];
        addRecentUser(recipient.id);
        log.user.action('user_added', 'RecipientPicker', { userId: recipient.id, name: recipient.name });
      } else {
        log.user.action('user_already_selected', 'RecipientPicker', { userId: recipient.id });
      }
    } else {
      if (!newSelection.groups.includes(recipient.id)) {
        newSelection.groups = [...newSelection.groups, recipient.id];
        addRecentGroup(recipient.id);
        log.user.action('group_added', 'RecipientPicker', { groupId: recipient.id });
      }
    }

    onChange(newSelection);
  }, [value, onChange, addRecentUser, addRecentGroup]);

  // Remove recipient handler
  const removeRecipient = useCallback((recipientId: string, type: 'user' | 'group') => {
    const newSelection = { ...value };

    if (type === 'user') {
      newSelection.users = newSelection.users.filter(id => id !== recipientId);
      log.user.action('user_removed', 'RecipientPicker', { userId: recipientId });
    } else {
      newSelection.groups = newSelection.groups.filter(id => id !== recipientId);
      log.user.action('group_removed', 'RecipientPicker', { groupId: recipientId });
    }

    onChange(newSelection);
  }, [value, onChange]);

  // Add multiple recipients
  const addRecipients = useCallback((recipients: Recipient[]) => {
    const newSelection = { ...value };

    recipients.forEach(recipient => {
      if (recipient.type === 'user' && !newSelection.users.includes(recipient.id)) {
        newSelection.users.push(recipient.id);
        addRecentUser(recipient.id);
      } else if (recipient.type === 'group' && !newSelection.groups.includes(recipient.id)) {
        newSelection.groups.push(recipient.id);
        addRecentGroup(recipient.id);
      }
    });

    log.user.action('recipients_added', 'RecipientPicker', {
      userCount: newSelection.users.length,
      groupCount: newSelection.groups.length
    });

    onChange(newSelection);
  }, [value, onChange, addRecentUser, addRecentGroup]);

  // Set complete selection
  const setSelection = useCallback((selection: RecipientSelection) => {
    onChange(selection);
  }, [onChange]);

  // Clear selection
  const clearSelection = useCallback(() => {
    onChange(RecipientUtils.empty());
    log.user.action('selection_cleared', 'RecipientPicker');
  }, [onChange]);

  return {
    // Data
    availableGroups,
    availableUsers,
    availableGroupsFiltered,
    availableUsersFiltered,
    selectedRecipients,
    recentGroups,
    recentUsers,

    // State
    isLoading,
    isLoadingGroups,
    isLoadingUsers,

    // Actions
    addRecipient,
    removeRecipient,
    addRecipients,
    setSelection,
    clearSelection,

    // Computed
    selectionCount: RecipientUtils.getCount(value),
    isEmpty: RecipientUtils.isEmpty(value),

    // Permissions
    canSeeAllGroups
  };
};
